import { clearAuthData, getStoredAuthData } from "../contexts/auth/authUtils";
import { REFRESH_TOKEN_STORAGE_KEY, TOKEN_STORAGE_KEY } from "../contexts/auth/constants";

// Constants
const API_URL = import.meta.env.VITE_APP_CONGNESS_API_URL as string;

// Types
interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
}

interface RefreshTokenResponse {
  token: string;
  refresh_token?: string;
}

// Token management
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
};

export const setTokens = (token: string, refreshToken?: string): void => {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  }
};

// Refresh token functionality
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/ld+json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data: RefreshTokenResponse = await response.json();
    setTokens(data.token, data.refresh_token);
    return data.token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Clear auth data on refresh failure
    clearAuthData();
    return null;
  }
};

// API request with token handling
export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  // Get the token
  const token = getToken();

  // Set up headers with token if available
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && !options.method?.toUpperCase().includes('DELETE')) {
    headers.set('Content-Type', 'application/ld+json');
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Create the request with headers
  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  try {
    // Make the initial request
    let response = await fetch(url, requestOptions);

    // If unauthorized and we have a refresh token, attempt to refresh
    if (response.status === 401 && getRefreshToken()) {
      const newToken = await refreshAccessToken();

      // If we got a new token, retry the original request
      if (newToken) {
        headers.set('Authorization', `Bearer ${newToken}`);
        const retryOptions = {
          ...requestOptions,
          headers,
        };

        response = await fetch(url, retryOptions);
      } else {
        // Redirect to login or handle as needed
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }
    }

    // Parse the response
    let data: T;
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/ld+json')) {
      data = await response.json();
    } else {
      data = {} as T;
    }

    if (!response.ok) {
      throw new Error(response.statusText || 'Request failed');
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error) {
    console.error(`API request error for ${endpoint}:`, error);
    throw error;
  }
};

// Convenience methods
export const get = <T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
};

export const post = <T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
};

export const put = <T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
};

export const patch = <T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      ...options?.headers,
      'Content-Type': 'application/merge-patch+json'
    }
  });
};

export const del = <T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> => {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
};
